import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, message, Radio, Switch } from 'antd';
import { User } from '../../interfaces/userManagement';
import orgManagementService from '../../apiServices/orgManagementService';
import { useAuthOrg } from '../../context/AuthOrgContext';
import { ROLE_OPTIONS, USER_ROLES } from '../../utils/roles';

interface UserFormModalProps {
    visible: boolean;
    onCancel: () => void;
    onSuccess: () => void;
    initialValues?: User | null;
}

const UserFormModal: React.FC<UserFormModalProps> = ({
    visible,
    onCancel,
    onSuccess,
    initialValues,
}) => {
    // console.log(initialValues);

    const [form] = Form.useForm();
    const { currentOrg, availableOrgs } = useAuthOrg();
    const [loading, setLoading] = useState(false);
    const [userMode, setUserMode] = useState<'self-served' | 'managed'>(initialValues?.userMode || 'self-served');

    useEffect(() => {
        if (visible && initialValues) {
            form.setFieldsValue({
                ...initialValues,
                email: initialValues.userEmail,
                role: initialValues.userRole,
                org_ids: initialValues.accessibleOrgs?.map(org => org.orgId)
            });
            // Infer userMode from initialValues
            if (initialValues.accessibleOrgs && initialValues.accessibleOrgs.length > 1) {
                setUserMode('managed');
            } else {
                // Default to self-served if 1 or 0 orgs, or if explicit mode is missing
                setUserMode('self-served');
            }
        } else {
            form.resetFields();
            setUserMode('self-served');
        }

    }, [visible, initialValues, form]);

    const handleOk = async () => {
        if (!currentOrg) return;

        try {
            const values = await form.validateFields();
            setLoading(true);

            if (initialValues) {
                // Update user access

                await orgManagementService.updateUserAccess({
                    userId: parseInt(initialValues.id),
                    orgIds: values.org_ids || [currentOrg.orgId]
                });
                message.success('User updated successfully');
            } else {
                // Add new user
                // Add new user
                let parentOrgId = currentOrg.orgId;
                let orgIds = values.org_ids || [currentOrg.orgId];

                if (userMode === 'self-served') {
                    // For self-served, parent_org_id is the chosen org (only one allowed)
                    // values.org_ids should be an array of 1
                    if (values.org_ids && values.org_ids.length > 0) {
                        parentOrgId = values.org_ids[0];
                        orgIds = [values.org_ids[0]];
                    }
                }

                await orgManagementService.addUser({
                    email: values.email || '',
                    user_mode: userMode,
                    user_type: 'external',
                    user_role: values.role || USER_ROLES.VIEWER,
                    mobile_number: values.mobile || '',
                    parent_org_id: parentOrgId,
                    org_ids: orgIds,
                    password: values.password || '',
                    user_name: values.userName || '',
                    view_pii_access: Boolean(values.viewPiiAccess),
                });
                message.success('Invitation sent successfully');
            }

            form.resetFields();
            onSuccess();
        } catch (error) {
            console.error('Error saving user:', error);
            message.error('Failed to save user');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={initialValues ? "Edit User" : "Add User"}
            open={visible}
            onOk={handleOk}
            onCancel={onCancel}
            confirmLoading={loading}
            okText={initialValues ? "Save Changes" : "Send Invitation"}
            okButtonProps={{ className: 'bg-[#1F344C]' }}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{ role: USER_ROLES.VIEWER, org_ids: [currentOrg?.orgId], userMode: 'self-served' }}
            >
                <Form.Item
                    name="userMode"
                    label="User Mode"
                    initialValue="self-served"
                >
                    <Radio.Group onChange={(e) => {
                        setUserMode(e.target.value);
                        form.setFieldsValue({ org_ids: [] }); // Reset org selection on mode change
                    }}>
                        <Radio value="self-served">Self-Served</Radio>
                        <Radio value="managed">Managed</Radio>
                    </Radio.Group>
                </Form.Item>
                <Form.Item
                    name="userName"
                    label="Full Name"
                    rules={[{ required: true, message: 'Please enter the user name' }]}
                >
                    <Input placeholder="John Doe" />
                </Form.Item>

                <Form.Item
                    name="email"
                    label="Email Address"
                    rules={[
                        { required: true, message: 'Please enter the email address' },
                        { type: 'email', message: 'Please enter a valid email' }
                    ]}
                >
                    <Input placeholder="john@example.com" disabled={!!initialValues} />
                </Form.Item>

                <Form.Item
                    name="password"
                    label="Password"
                    rules={[{ required: true, message: 'Please enter the password' }]}
                >
                    <Input placeholder="password" />
                </Form.Item>

                <Form.Item
                    name="role"
                    label="Role"
                    rules={[{ required: true, message: 'Please select a role' }]}
                >
                    <Select>
                        {ROLE_OPTIONS.map((role) => (
                            <Select.Option key={role.value} value={role.value}>
                                {role.label}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="org_ids"
                    label="Accessible Accounts"
                    rules={[{ required: true, message: 'Please select at least one account' }]}
                >
                    <Select
                        mode={"multiple"}
                        placeholder={userMode === 'self-served' ? "Select account" : "Select accounts"}
                        style={{ width: '100%' }}
                        maxCount={userMode === 'self-served' ? 1 : undefined}
                    >
                        {availableOrgs.map(org => (
                            <Select.Option key={org.orgId} value={org.orgId}>
                                {org.orgName}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="mobile"
                    label="Mobile Number (Optional)"
                >
                    <Input placeholder="+1234567890" />
                </Form.Item>
                <Form.Item
                    name="viewPiiAccess"
                    label="PII access"
                    valuePropName="checked"
                    initialValue={false}
                >
                    <Switch checkedChildren="Allowed" unCheckedChildren="Not allowed" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default UserFormModal;
